import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { AdminEquipmentService } from '@app/api/api';
import { environment } from 'environments/environment';
import { ModalViewUserEquipmentComponent } from './modal-view-user-equipment/modal-view-user-equipment.component';
import moment from 'moment';

@Component({
  selector: 'meu-admin-my-equipment-management',
  templateUrl: './admin-my-equipment-management.component.html',
  styleUrls: ['./admin-my-equipment-management.component.scss'],
})
export class AdminMyEquipmentManagementComponent implements OnInit {
  constructor(
    private toastr: ToastrService,
    private ref: ChangeDetectorRef,
    private adminEquipmentService: AdminEquipmentService,
    public dialog: MatDialog
  ) {}

  displayedColumns: string[] = [
    'Index',
    'Image',
    'Name',
    'User',
    'Brand',
    'Model',
    'SerialNumber',
    'Location',
    'Status',
    'Actions',
  ];

  lstAll: any[] = [];
  total = 0;
  pageSize = 10;
  pageIndex = 1;
  host = '';
  filter = '';
  searchName = '';
  pageSizeOptions: number[] = [5, 10, 20, 100];

  // Date filter (Mặc định: Năm nay)
  customStartDate: any = moment().startOf('year').toDate();
  customEndDate: any = new Date();
  selectedDateType = 'this_year';
  dateTypeOptions = [
    { label: 'Tất cả', value: 'all' },
    { label: 'Hôm nay', value: 'today' },
    { label: '7 ngày gần nhất', value: 'last_7_days' },
    { label: '30 ngày gần nhất', value: 'last_30_days' },
    { label: 'Tuần này', value: 'this_week' },
    { label: 'Tháng này', value: 'this_month' },
    { label: 'Quý này', value: 'this_quarter' },
    { label: 'Năm nay', value: 'this_year' },
    { label: 'Tùy chọn', value: 'custom' },
  ];

  public keyUp = new Subject<KeyboardEvent>();

  ngOnInit() {
    this.host = environment.backendhost;
    this.keyUp
      .pipe(
        map((event: any) => event),
        debounceTime(500)
      )
      .subscribe((value: string) => {
        this.pageIndex = 1;
        this.searchName = (value || '').trim();
        this.getAll();
      });
    this.getAll();
  }

  getDateRange(): { startDate?: string; endDate?: string } {
    if (!this.selectedDateType || this.selectedDateType === 'all') {
      return {};
    }
    switch (this.selectedDateType) {
      case 'today': {
        const today = moment().format('YYYY-MM-DD');
        return { startDate: today, endDate: today };
      }
      case 'last_7_days':
        return {
          startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
          endDate: moment().format('YYYY-MM-DD'),
        };
      case 'last_30_days':
        return {
          startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
          endDate: moment().format('YYYY-MM-DD'),
        };
      case 'this_week':
        return {
          startDate: moment().startOf('isoWeek').format('YYYY-MM-DD'),
          endDate: moment().endOf('isoWeek').format('YYYY-MM-DD'),
        };
      case 'this_month':
        return {
          startDate: moment().startOf('month').format('YYYY-MM-DD'),
          endDate: moment().endOf('month').format('YYYY-MM-DD'),
        };
      case 'this_quarter':
        return {
          startDate: moment().startOf('quarter').format('YYYY-MM-DD'),
          endDate: moment().endOf('quarter').format('YYYY-MM-DD'),
        };
      case 'this_year':
        return {
          startDate: moment().startOf('year').format('YYYY-MM-DD'),
          endDate: moment().endOf('year').format('YYYY-MM-DD'),
        };
      case 'custom': {
        const startDate = this.customStartDate
          ? moment(this.customStartDate).format('YYYY-MM-DD')
          : undefined;
        const endDate = this.customEndDate
          ? moment(this.customEndDate).format('YYYY-MM-DD')
          : undefined;
        return { startDate, endDate };
      }
      default:
        return {};
    }
  }

  onDateTypeChange() {
    this.pageIndex = 1;
    this.getAll();
  }

  onCustomDateChange() {
    this.pageIndex = 1;
    this.getAll();
  }

  getAll() {
    const { startDate, endDate } = this.getDateRange();
    this.adminEquipmentService
      .apiAdminMyEquipmentGet('', '', this.pageIndex, this.pageSize, this.searchName, startDate, endDate)
      .subscribe((res) => {
        if (res.success) {
          this.lstAll = res.data.collection || [];
          this.total = res.data.total || 0;
        } else if (Array.isArray(res)) {
          this.lstAll = res;
          this.total = res.length;
        } else {
          this.toastr.error(res.message || 'Lỗi khi tải danh sách thiết bị');
        }
        this.ref.detectChanges();
      });
  }

  onPageChange(pageNumber: number) {
    if (typeof pageNumber === 'number') {
      this.pageIndex = pageNumber;
      this.getAll();
      window.scrollTo(0, 0);
    }
  }

  onPageSizeChange(newSize: number) {
    this.pageSize = newSize;
    this.pageIndex = 1;
    this.getAll();
  }

  getImage(element: any): string {
    const model = element.extend_equipment_model || {};
    const img = element.default_image || model.default_image || '';
    if (!img) {
      return '';
    }
    if (img.indexOf('http') === 0) {
      return img;
    }
    return this.host + img;
  }

  getUserFullName(element: any): string {
    const user = element.extend_user || {};
    return user.full_name || user.email || '';
  }

  getBrandName(element: any): string {
    const brand = element.extend_brand || {};
    return element.brand_name_manual || brand.name || '';
  }

  getModelCode(element: any): string {
    const model = element.extend_equipment_model || {};
    return element.model_name_manual || model.model_code || '';
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'active':
        return 'Đang hoạt động';
      case 'inactive':
        return 'Ngừng hoạt động';
      case 'maintenance':
        return 'Bảo trì';
      case 'retired':
        return 'Ngừng sử dụng';
      default:
        return status || '';
    }
  }

  viewDetail(element: any) {
    const user = element.extend_user || {};
    const userId = element.user_id || user.id || '';
    const dialogRef = this.dialog.open(ModalViewUserEquipmentComponent, {
      width: '1100px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        userId,
        equipment: element,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      // Refresh list if an edit happened inside the view modal
      if (result && result.event) {
        this.getAll();
      }
    });
  }
}
